/**
 * nav.ts — THE FEED global chrome behaviour (FINAL-SPEC §3.1, §3.2, §6.x):
 *  - the cover hand-off: on the homepage the bar is transparent while
 *    scrollY < 80 and solid after (`.is-solid` on header.site-nav), driven by an
 *    IntersectionObserver on an 80px sentinel — no extra scroll listener;
 *  - `--nav-h` on <html> (the measured header height, nav + story bar) for
 *    sticky stages and scroll-margin, kept fresh by a ResizeObserver;
 *  - the mobile menu sheet: open/close, aria-expanded, focus trap, Esc,
 *    scroll lock on <html>, close on link tap and when the viewport grows
 *    past the burger breakpoint;
 *  - every theme switch's aria-pressed mirrors <html data-theme> on load (the
 *    footer copy is rendered without the theme; public.ts flips them all).
 * The theme switch itself lives in public.ts `initThemeToggle`; the story bar's
 * progress + aria-current in home/runtime.ts.
 */

const SOLID_AFTER_PX = 80;
const WIDE = "(min-width: 1024px)";

function reducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function initSolidState(header: HTMLElement): void {
  if (!document.body.classList.contains("page-home")) return;
  const set = (solid: boolean): void => {
    header.classList.toggle("is-solid", solid);
  };
  set(window.scrollY >= SOLID_AFTER_PX);
  if (!("IntersectionObserver" in window)) {
    set(true);
    return;
  }
  const sentinel = document.createElement("div");
  sentinel.className = "nav-sentinel";
  sentinel.setAttribute("aria-hidden", "true");
  document.body.prepend(sentinel);
  new IntersectionObserver(
    (entries) => {
      for (const entry of entries) set(!entry.isIntersecting);
    },
    { threshold: 0 },
  ).observe(sentinel);
}

function initNavHeight(header: HTMLElement): void {
  const root = document.documentElement;
  const write = (): void => {
    root.style.setProperty(
      "--nav-h",
      `${Math.round(header.getBoundingClientRect().height)}px`,
    );
  };
  write();
  if ("ResizeObserver" in window) new ResizeObserver(write).observe(header);
  else window.addEventListener("resize", write, { passive: true });
}

function syncThemeButtons(): void {
  const dark = document.documentElement.getAttribute("data-theme") === "dark";
  for (const b of document.querySelectorAll<HTMLElement>("[data-theme-toggle]"))
    b.setAttribute("aria-pressed", String(dark));
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

function initSheet(): void {
  const sheet = document.querySelector<HTMLElement>("[data-sheet]");
  const opener = document.querySelector<HTMLButtonElement>("[data-sheet-open]");
  if (!sheet || !opener) return;
  const closer = sheet.querySelector<HTMLButtonElement>("[data-sheet-close]");
  const root = document.documentElement;
  const openLabel = opener.getAttribute("aria-label") ?? "";
  const closeLabel = closer?.getAttribute("aria-label") ?? openLabel;
  let open = false;
  let hideTimer = 0;
  // aria-modal alone is not honoured by every screen reader: make the page
  // behind the sheet inert while it is open (the header's bar, not the sheet)
  const behind = (): HTMLElement[] =>
    Array.from(
      document.querySelectorAll<HTMLElement>(
        "main, footer, header.site-nav .nav__bar, header.site-nav .story",
      ),
    ).filter((el) => !el.contains(sheet));
  const setInert = (on: boolean): void => {
    for (const el of behind()) el.inert = on;
  };

  const focusables = (): HTMLElement[] =>
    Array.from(sheet.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );

  const onKey = (event: KeyboardEvent): void => {
    if (!open) return;
    if (event.key === "Escape") {
      event.preventDefault();
      close(true);
      return;
    }
    if (event.key !== "Tab") return;
    const list = focusables();
    if (list.length === 0) return;
    const first = list[0];
    const last = list[list.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (event.shiftKey && (active === first || !sheet.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (
      !event.shiftKey &&
      (active === last || !sheet.contains(active))
    ) {
      event.preventDefault();
      first.focus();
    }
  };

  function show(): void {
    if (open) return;
    open = true;
    window.clearTimeout(hideTimer);
    sheet!.hidden = false;
    root.classList.add("sheet-open");
    opener!.setAttribute("aria-expanded", "true");
    opener!.setAttribute("aria-label", closeLabel);
    setInert(true);
    document.addEventListener("keydown", onKey);
    // two frames so the closed state commits before the open transition
    requestAnimationFrame(() =>
      requestAnimationFrame(() => sheet!.classList.add("is-open")),
    );
    (closer ?? focusables()[0])?.focus({ preventScroll: true });
  }

  function close(returnFocus: boolean): void {
    if (!open) return;
    open = false;
    sheet!.classList.remove("is-open");
    root.classList.remove("sheet-open");
    setInert(false);
    opener!.setAttribute("aria-expanded", "false");
    opener!.setAttribute("aria-label", openLabel);
    document.removeEventListener("keydown", onKey);
    const finish = (): void => {
      if (!open) sheet!.hidden = true;
    };
    if (reducedMotion() || !root.classList.contains("js-motion")) finish();
    else hideTimer = window.setTimeout(finish, 200);
    if (returnFocus) opener!.focus({ preventScroll: true });
  }

  opener.addEventListener("click", () => (open ? close(true) : show()));
  closer?.addEventListener("click", () => close(true));
  // a link tap closes the sheet (same-page anchors then scroll as usual)
  for (const link of sheet.querySelectorAll<HTMLAnchorElement>("a[href]")) {
    link.addEventListener("click", () => close(false));
  }
  const wide = window.matchMedia(WIDE);
  const onWide = (): void => {
    if (wide.matches) close(false);
  };
  if (typeof wide.addEventListener === "function")
    wide.addEventListener("change", onWide);
}

/* A story-bar chapter tap is turned into a smooth scroll by home/runtime.ts
   (preventDefault), which loses the native "move the focus start point" of an
   in-page anchor — move focus to the chapter so the next Tab continues there. */
function initStoryFocus(header: HTMLElement): void {
  const bar = header.querySelector<HTMLElement>(".story");
  if (!bar) return;
  bar.addEventListener("click", (event) => {
    const seg = (event.target as Element | null)?.closest?.("a.story-seg");
    if (!seg || !event.defaultPrevented) return;
    const id = (seg.getAttribute("href") ?? "").replace(/^.*#/, "");
    const target = id ? document.getElementById(id) : null;
    if (!target) return;
    if (!target.hasAttribute("tabindex")) {
      target.setAttribute("tabindex", "-1");
      target.setAttribute("data-story-focus", "");
    }
    target.focus({ preventScroll: true });
  });
}

export function initNav(): void {
  const header = document.querySelector<HTMLElement>("header.site-nav");
  syncThemeButtons();
  if (!header) return;
  initSolidState(header);
  initNavHeight(header);
  initSheet();
  initStoryFocus(header);
}
